import { useMemo, useState } from "react";
import { useTasksDb } from "../../hooks/useDb";
import { useDataHelpers } from "../../hooks/useDataHelpers";
import { fmtDMY } from "../../utils/date";
import { useAssignmentApprovalsQuery } from "../../api/queries/assignment-approvals";
import {
  useDraftsTasksQuery,
  useDeleteTaskMutation,
} from "../../api/queries/tasks";
import { useDeleteProjectMutation } from "../../api/queries/projects";
import { useConfirm } from "../../context/ConfirmContext";
import { useToast } from "../../context/ToastContext";

/**
 * Раздел «Черновики».
 *
 * Показывает ЛИЧНЫЕ черновики пользователя (status: 'draft').
 * Реализовано пакетное удаление через чекбоксы для задач и проектов.
 * Удаление происходит через Promise.allSettled для обработки частичных ошибок.
 */
function DraftsView({ ur, openTask, openProject }) {
  const db = useTasksDb();
  const { projects } = db;
  const { empName } = useDataHelpers(db);
  const { confirm } = useConfirm();
  const { showToast } = useToast();

  const { data: tasks = [] } = useDraftsTasksQuery();
  const { data: allApprovals = [] } = useAssignmentApprovalsQuery();

  const deleteTaskMutation = useDeleteTaskMutation();
  const deleteProjectMutation = useDeleteProjectMutation();

  const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
  const [selectedProjectIds, setSelectedProjectIds] = useState(() => new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const approvalsByTarget = useMemo(() => {
    const map = new Map();
    for (const a of allApprovals) {
      if (a.status !== "pending") continue;
      map.set(`${a.targetType}:${a.targetId}`, a);
    }
    return map;
  }, [allApprovals]);

  const taskDrafts = useMemo(() => {
    return tasks
      .filter((t) => t.status === "draft" && t.creatorId === ur.id)
      .map((t) => ({
        task: t,
        approval: approvalsByTarget.get(`task:${t.id}`) || null,
      }));
  }, [tasks, approvalsByTarget, ur.id]);

  const projectDrafts = useMemo(() => {
    return projects
      .filter((p) => p.status === "draft" && p.creatorId === ur.id)
      .map((p) => ({
        project: p,
        approval: approvalsByTarget.get(`project:${p.id}`) || null,
      }));
  }, [projects, approvalsByTarget, ur.id]);

  const total = taskDrafts.length + projectDrafts.length;

  const statusLabel = (approval) =>
    approval ? "На согласовании" : "Личный черновик";

  const approverLabel = (approval) =>
    approval ? empName(approval.approverId) : "-";

  const handleSelectAllTasks = (e) => {
    if (e.target.checked) {
      setSelectedTaskIds(new Set(taskDrafts.map((t) => t.task.id)));
    } else {
      setSelectedTaskIds(new Set());
    }
  };

  const handleSelectTask = (e, id) => {
    e.stopPropagation();
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (e.target.checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAllProjects = (e) => {
    if (e.target.checked) {
      setSelectedProjectIds(new Set(projectDrafts.map((p) => p.project.id)));
    } else {
      setSelectedProjectIds(new Set());
    }
  };

  const handleSelectProject = (e, id) => {
    e.stopPropagation();
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (e.target.checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const totalSelected = selectedTaskIds.size + selectedProjectIds.size;
    if (totalSelected === 0) return;

    const ok = await confirm({
      title: "Удалить выбранные черновики",
      message: `Вы уверены, что хотите безвозвратно удалить ${totalSelected} черновик(ов)?`,
      confirmLabel: "Удалить",
      danger: true,
    });
    if (!ok) return;

    setIsBulkDeleting(true);
    const taskPromises = Array.from(selectedTaskIds).map((id) =>
      deleteTaskMutation.mutateAsync({ id }),
    );
    const projectPromises = Array.from(selectedProjectIds).map((id) =>
      deleteProjectMutation.mutateAsync(id),
    );

    const results = await Promise.allSettled([
      ...taskPromises,
      ...projectPromises,
    ]);
    const failedCount = results.filter((r) => r.status === "rejected").length;
    const successCount = totalSelected - failedCount;

    if (failedCount > 0) {
      showToast(`Удалено: ${successCount}. Ошибок: ${failedCount}`, "warning");
    } else {
      showToast(`Успешно удалено черновиков: ${successCount}`, "success");
    }

    setSelectedTaskIds(new Set());
    setSelectedProjectIds(new Set());
    setIsBulkDeleting(false);
  };

  const allTasksSelected =
    taskDrafts.length > 0 && selectedTaskIds.size === taskDrafts.length;
  const allProjectsSelected =
    projectDrafts.length > 0 &&
    selectedProjectIds.size === projectDrafts.length;

  return (
    <div className="rep">
      <div className="rep-panel p-4">
        <div className="rep-panel-title flex justify-between items-center">
          <span>Черновики ({total})</span>
          {(selectedTaskIds.size > 0 || selectedProjectIds.size > 0) && (
            <button
              type="button"
              className="btn danger sm"
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
            >
              {isBulkDeleting
                ? "Удаление..."
                : `Удалить выбранные (${selectedTaskIds.size + selectedProjectIds.size})`}
            </button>
          )}
        </div>
        <p className="mut sm">
          Ваши личные черновики задач и проектов. Их видите только вы - до
          сохранения в рабочий статус или отправки на согласование они никому не
          показываются.
        </p>
      </div>

      <div className="rep-panel p-4">
        <div className="rep-panel-title">Задачи ({taskDrafts.length})</div>
        {taskDrafts.length === 0 ? (
          <div className="mut sm">Черновиков задач нет</div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="w-10">
                    <input
                      type="checkbox"
                      checked={allTasksSelected}
                      onChange={handleSelectAllTasks}
                      title="Выбрать все задачи"
                    />
                  </th>
                  <th>Название</th>
                  <th>Исполнитель</th>
                  <th>Статус</th>
                  <th>Согласующий</th>
                  <th>Создано</th>
                </tr>
              </thead>
              <tbody>
                {taskDrafts.map(({ task, approval }) => (
                  <tr
                    key={task.id}
                    className="clickable-row"
                    onClick={() => openTask(task.id)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedTaskIds.has(task.id)}
                        onChange={(e) => handleSelectTask(e, task.id)}
                      />
                    </td>
                    <td>
                      <b>{task.title || "(без названия)"}</b>
                    </td>
                    <td>{task.assigneeId ? empName(task.assigneeId) : "-"}</td>
                    <td>
                      <span
                        className={"st-chip " + (approval ? "pending" : "")}
                      >
                        {statusLabel(approval)}
                      </span>
                    </td>
                    <td>{approverLabel(approval)}</td>
                    <td className="mut sm">{fmtDMY(task.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rep-panel p-4">
        <div className="rep-panel-title">Проекты ({projectDrafts.length})</div>
        {projectDrafts.length === 0 ? (
          <div className="mut sm">Черновиков проектов нет</div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="w-10">
                    <input
                      type="checkbox"
                      checked={allProjectsSelected}
                      onChange={handleSelectAllProjects}
                      title="Выбрать все проекты"
                    />
                  </th>
                  <th>Название</th>
                  <th>Код</th>
                  <th>Ответственный</th>
                  <th>Статус</th>
                  <th>Согласующий</th>
                  <th>Создано</th>
                </tr>
              </thead>
              <tbody>
                {projectDrafts.map(({ project, approval }) => (
                  <tr
                    key={project.id}
                    className="clickable-row"
                    onClick={() => openProject(project.id)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedProjectIds.has(project.id)}
                        onChange={(e) => handleSelectProject(e, project.id)}
                      />
                    </td>
                    <td>
                      <b>{project.name || "(без названия)"}</b>
                    </td>
                    <td>{project.code || "-"}</td>
                    <td>
                      {project.managerId ? empName(project.managerId) : "-"}
                    </td>
                    <td>
                      <span
                        className={"st-chip " + (approval ? "pending" : "")}
                      >
                        {statusLabel(approval)}
                      </span>
                    </td>
                    <td>{approverLabel(approval)}</td>
                    <td className="mut sm">{fmtDMY(project.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DraftsView;
